import { X } from 'lucide-react'
import { Skeleton } from '@/components/Skeleton'
import type { LinkCardMetadata } from '@/types/linkCard'

interface LinkCardPreviewProps {
    metadata: LinkCardMetadata | null
    isLoading: boolean
    onDismiss: () => void
}

export function LinkCardPreview({ metadata, isLoading, onDismiss }: LinkCardPreviewProps) {
    if (isLoading) {
        return (
            <div className="border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
                <Skeleton className="w-full h-32 !rounded-none" />
                <div className="p-3 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-1/3" />
                </div>
            </div>
        )
    }

    if (!metadata) return null

    const domain = (() => {
        try {
            return new URL(metadata.url).hostname
        } catch {
            return metadata.url
        }
    })()

    return (
        <div className="relative border border-border-light dark:border-border-dark rounded-lg overflow-hidden">
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Remove link card"
                className="absolute top-2 right-2 z-10 p-1 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
            >
                <X className="w-4 h-4 text-white" />
            </button>
            {metadata.image && (
                <img
                    src={metadata.image}
                    alt={metadata.title || ''}
                    className="w-full h-32 object-cover"
                    onError={e => {
                        e.currentTarget.style.display = 'none'
                    }}
                />
            )}
            <div className="p-3">
                {metadata.title && (
                    <p className="text-sm font-semibold text-text-primary-light dark:text-text-primary-dark line-clamp-1">
                        {metadata.title}
                    </p>
                )}
                {metadata.description && (
                    <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark line-clamp-2 mt-0.5">
                        {metadata.description}
                    </p>
                )}
                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1 truncate">
                    {domain}
                </p>
            </div>
        </div>
    )
}
